import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { setModal } from 'modules/_shared/redux/modalActions';
import Button from 'modules/_shared/components/Buttons';

type HowItWorksModalProps = {
  size?: string;
  societyId?: string;
};

const HowItWorksModal: FC<HowItWorksModalProps> = ({ size, societyId }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const history = useHistory();
  const location = useLocation();
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const pq = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const navigateTo = (path: string) => {
    handleClose();
    if (societyId) history.push(path);
  };

  const goToCreateSellOffer = () => {
    navigateTo(`/crear-oferta-venta-incentivos/${societyId ?? actualSociety._id}${pq}`);
  };

  const goToViewSellOffers = () => {
    navigateTo(`/ofertas-venta-incentivos/${societyId ?? actualSociety._id}${pq}`);
  };

  const goToCreateBuyOffer = () => {
    navigateTo(`/crear-oferta-compra-incentivos/${societyId ?? actualSociety._id}${pq}`);
  };

  return (
    <>
      <Modal.Header className="border-bottom">
        <Modal.Title className="mb-0">{t('IncentivesMarketHowItWorksModalTitle')}</Modal.Title>
        <a
          className="close cursor-pointer"
          onClick={handleClose}
          aria-label={t('Close')}
        >
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-4">{t('IncentivesMarketHowItWorksModalLead')}</p>

        <h6 className="overline-title text-info mb-3">
          {t('IncentivesMarketHowItWorksModalSellSectionTitle')}
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-info-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-offer text-info" />
            </div>
            <div>
              <strong>{t('IncentivesMarketHowItWorksModalSellStep1Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('IncentivesMarketHowItWorksModalSellStep1Description')}
              </p>
              <Button
                variant="link"
                className="link link-info mt-2 btn btn-link p-0 text-start"
                onClick={goToCreateSellOffer}
                disabled={!isAdmin}
              >
                {t('IncentivesMarketHowItWorksModalConfigureSellOffer')}{' '}
                <em className="icon ni ni-arrow-long-right ml-1" />
              </Button>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-info-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-users text-info" />
            </div>
            <div>
              <strong>{t('IncentivesMarketHowItWorksModalSellStep2Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('IncentivesMarketHowItWorksModalSellStep2Description')}
              </p>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-info-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-check-circle text-info" />
            </div>
            <div>
              <strong>{t('IncentivesMarketHowItWorksModalSellStep3Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('IncentivesMarketHowItWorksModalSellStep3Description')}
              </p>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-info-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-swap text-info" />
            </div>
            <div>
              <strong>{t('IncentivesMarketHowItWorksModalSellStep4Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('IncentivesMarketHowItWorksModalSellStep4Description')}
              </p>
            </div>
          </li>
        </ul>

        <h6 className="overline-title text-primary mb-3">
          {t('IncentivesMarketHowItWorksModalBuySectionTitle')}
        </h6>
        <p className="text-soft mb-3 small">{t('IncentivesMarketHowItWorksModalBuyIntro')}</p>
        <ul className="list-group list-group-flush">
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-primary-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-cart text-primary" />
            </div>
            <div>
              <strong>{t('IncentivesMarketHowItWorksModalBuyOption1Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('IncentivesMarketHowItWorksModalBuyOption1Description')}
              </p>
              <Button
                variant="link"
                className="link link-primary mt-2 btn btn-link p-0 text-start"
                onClick={goToViewSellOffers}
              >
                {t('IncentivesMarketHowItWorksModalViewSellOffers')}{' '}
                <em className="icon ni ni-arrow-long-right ml-1" />
              </Button>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-primary-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-offer text-primary" />
            </div>
            <div>
              <strong>{t('IncentivesMarketHowItWorksModalBuyOption2Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('IncentivesMarketHowItWorksModalBuyOption2Description')}
              </p>
              <Button
                variant="link"
                className="link link-primary mt-2 btn btn-link p-0 text-start"
                onClick={goToCreateBuyOffer}
                disabled={!isAdmin}
              >
                {t('IncentivesMarketHowItWorksModalCreateBuyOffer')}{' '}
                <em className="icon ni ni-arrow-long-right ml-1" />
              </Button>
            </div>
          </li>
        </ul>
      </Modal.Body>

      <Modal.Footer className="border-top bg-lighter">
        <span className="text-soft fs-12px me-auto">
          {t('IncentivesMarketHowItWorksModalFooterHelp')}
        </span>
        <Button onClick={handleClose}>{t('IncentivesMarketHowItWorksModalUnderstood')}</Button>
      </Modal.Footer>
    </>
  );
};

export default HowItWorksModal;
